import { slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import ReactDatePicker from '@costar/react-datepicker';
import { utils, useSafeThemeUI, useKeyPress, Grid, Input, FlexInline, Text, Box, FormField, Collapse } from '@costar/theme-ui';
import parseISO from 'date-fns/parseISO';
import React, { useState, useMemo, useCallback } from 'react';
import { NULL_RANGE } from './constants.esm.js';
import getDefaultDateFormat from '../../utils/get-default-date-format.esm.js';
import getLocaleDateFormat from '../../utils/get-locale-date-format.esm.js';
import { addYearsToNow, getDateInputMask } from '../../utils/dates.esm.js';
import { useDateMask } from '../../hooks/use-date-mask.esm.js';
import { dayHighlightingHelper } from '../../utils/highlight-dates.esm.js';
import { CustomHeader } from '../custom-header/custom-header.esm.js';

// import { useWhatChanged } from '@simbathesailor/use-what-changed'

function getRenderCustomHeader(props) {
  return function PrivateCustomHeader(params) {
    return /*#__PURE__*/React.createElement(CustomHeader, _extends({}, props, params));
  };
}
var PrivateDatePicker = function PrivateDatePicker(props) {
  var maxDate = props.maxDate,
    minDate = props.minDate,
    locale = props.locale,
    selected = props.selected,
    fontSize = props.fontSize,
    fontFamily = props.fontFamily;
  var internalSelected = useMemo(function () {
    return selected;
  }, [selected]);
  return /*#__PURE__*/React.createElement(ReactDatePicker, _extends({
    renderCustomHeader: getRenderCustomHeader({
      selected: internalSelected,
      maxDate: maxDate,
      minDate: minDate,
      locale: locale,
      showMonthSelect: true,
      fontSize: fontSize,
      fontFamily: fontFamily
    })
  }, props, {
    locale: locale
  }));
};
var getUpArrowStyle = function getUpArrowStyle(theme) {
  var _theme$transitions;
  return {
    position: 'absolute',
    marginTop: '-0.95em',
    marginLeft: 4,
    marginRight: 4,
    width: '2em',
    height: '1em',
    transition: (_theme$transitions = theme.transitions) === null || _theme$transitions === void 0 ? void 0 : _theme$transitions.create('left'),
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '0 1em 1em 1em',
      borderColor: 'transparent transparent currentcolor transparent'
    }
  };
};
var DateRangePicker = function DateRangePicker(props) {
  var _props$locale = props.locale,
    locale = _props$locale === void 0 ? 'en-US' : _props$locale,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? utils.noop : _props$onChange,
    _props$maxDate = props.maxDate,
    maxDate = _props$maxDate === void 0 ? addYearsToNow(100) : _props$maxDate,
    dateFormatProps = props.dateFormat,
    _props$inputProps = props.inputProps;
    _props$inputProps === void 0 ? utils.EMPTY_OBJECT : _props$inputProps;
    var _props$minDate = props.minDate,
    minDate = _props$minDate === void 0 ? new Date(1980, 0, 1) : _props$minDate,
    selectedDatesISOString = props.selectedDatesISOString,
    label = props.label,
    fontSize = props.fontSize,
    fontFamily = props.fontFamily,
    onDatePickerExiting = props.onDatePickerExiting,
    onDatePickerExited = props.onDatePickerExited,
    placeholder = props.placeholder,
    _props$formatWidth = props.formatWidth,
    formatWidth = _props$formatWidth === void 0 ? 'short' : _props$formatWidth;
  var theme = useSafeThemeUI(props);
  var upArrowStyle = getUpArrowStyle(theme);
  var shortDateFormat = getLocaleDateFormat(locale, formatWidth); //this format is not consistent with resources. datePickerInternal
  var _getDateInputMask = getDateInputMask(shortDateFormat),
    maskedPlaceholder = _getDateInputMask.placeHolder;
  var dateFormat = getDefaultDateFormat(locale, dateFormatProps);
  var _useState = useState(new Date()),
    _useState2 = _slicedToArray(_useState, 2),
    calendarDate = _useState2[0],
    setCalendarDate = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    calendarMode = _useState4[0],
    setCalendarMode = _useState4[1];
  var _useState5 = useState(new Date()),
    _useState6 = _slicedToArray(_useState5, 2),
    visibleMonthDate = _useState6[0],
    setVisibleMonthDate = _useState6[1];
  var maskOptions = useDateMask({
    dateFormat: shortDateFormat,
    minDate: minDate,
    maxDate: maxDate
  });
  var _useKeyPress = useKeyPress('enter'),
    _useKeyPress2 = _slicedToArray(_useKeyPress, 2),
    enterKeyPressed = _useKeyPress2[0];
    _useKeyPress2[1];
  var selectedDates = useMemo(function () {
    var minValue = selectedDatesISOString !== null && selectedDatesISOString !== void 0 && selectedDatesISOString.minValue ? parseISO(selectedDatesISOString === null || selectedDatesISOString === void 0 ? void 0 : selectedDatesISOString.minValue) : undefined;
    var maxValue = selectedDatesISOString !== null && selectedDatesISOString !== void 0 && selectedDatesISOString.maxValue ? parseISO(selectedDatesISOString === null || selectedDatesISOString === void 0 ? void 0 : selectedDatesISOString.maxValue) : undefined;
    return {
      minValue: minValue,
      maxValue: maxValue
    };
  }, [selectedDatesISOString]);
  var handleStartDateOnFocus = useCallback(function () {
    if (selectedDates !== null && selectedDates !== void 0 && selectedDates.minValue) {
      setCalendarDate(selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.minValue);
    } else if (selectedDates !== null && selectedDates !== void 0 && selectedDates.maxValue) {
      setCalendarDate(selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.maxValue);
    } else setCalendarDate(new Date());
    if (selectedDates.minValue) {
      setVisibleMonthDate(selectedDates.minValue);
    } else {
      setVisibleMonthDate(minDate);
    }
    setCalendarMode('start');
  }, [minDate, selectedDates]);
  var handleEndDateOnFocus = useCallback(function () {
    if (selectedDates !== null && selectedDates !== void 0 && selectedDates.maxValue) {
      setCalendarDate(selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.maxValue);
    } else if (selectedDates !== null && selectedDates !== void 0 && selectedDates.minValue) {
      setCalendarDate(selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.minValue);
    } else {
      setCalendarDate(new Date());
    }
    if (selectedDates.maxValue) {
      setVisibleMonthDate(selectedDates.maxValue);
    } else {
      setVisibleMonthDate(maxDate);
    }
    setCalendarMode('end');
  }, [maxDate, selectedDates]);
  var processChanges = useCallback(function (mode, date) {
    var payload = NULL_RANGE;
    switch (mode) {
      case 'start':
        if (date && selectedDates !== null && selectedDates !== void 0 && selectedDates.maxValue && selectedDates.maxValue.getTime() < date.getTime()) {
          payload = {
            minValue: selectedDatesISOString === null || selectedDatesISOString === void 0 ? void 0 : selectedDatesISOString.maxValue,
            maxValue: date && date.toISOString()
          };
        } else {
          payload = {
            minValue: date && date.toISOString(),
            maxValue: selectedDatesISOString === null || selectedDatesISOString === void 0 ? void 0 : selectedDatesISOString.maxValue
          };
        }
        break;
      case 'end':
        if (date && selectedDates !== null && selectedDates !== void 0 && selectedDates.minValue && (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.minValue.getTime()) > date.getTime()) {
          payload = {
            minValue: date && date.toISOString(),
            maxValue: selectedDatesISOString === null || selectedDatesISOString === void 0 ? void 0 : selectedDatesISOString.minValue
          };
        } else {
          payload = {
            minValue: selectedDatesISOString === null || selectedDatesISOString === void 0 ? void 0 : selectedDatesISOString.minValue,
            maxValue: date && date.toISOString()
          };
        }
        break;
    }
    onChange(payload);
  }, [selectedDatesISOString, selectedDates, onChange]);
  var handleStartDatePickerOnSelect = useCallback(function (value) {
    value.setHours(0, 0, 0, 0);
    setCalendarMode(undefined);
    processChanges('start', value);
  }, [processChanges]);
  var handleEndDatePickerOnSelect = useCallback(function (value) {
    value.setHours(23, 59, 59, 999);
    setCalendarMode(undefined);
    processChanges('end', value);
  }, [processChanges]);
  var highlightDates = useMemo(function () {
    var h = dayHighlightingHelper({
      visibleCalendarDate: visibleMonthDate,
      selectedRange: {
        min: (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.minValue) || undefined,
        max: (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.maxValue) || undefined
      },
      range: {
        min: minDate || new Date(1980, 0, 1),
        max: maxDate || new Date()
      },
      focusedInput: calendarMode || 'start'
    });
    return h.length ? h : [new Date()]; //default highlight today
  }, [visibleMonthDate, selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.minValue, selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.maxValue, minDate, maxDate, calendarMode]);
  var handleOnKeypress = useCallback(function (mode) {
    return function (event) {
      if (event && enterKeyPressed && !calendarMode) {
        setCalendarMode(mode);
      }
      if (event && enterKeyPressed && calendarMode) {
        setCalendarMode(undefined);
      }
    };
  }, [calendarMode, enterKeyPressed]);
  var handleOnDateChangeRaw = useCallback(function (mode) {
    return function (event) {
      var _maskEvent$masked;
      if (!calendarMode) {
        setCalendarMode(mode);
      }
      var maskEvent = event;
      var newDate = maskEvent === null || maskEvent === void 0 ? void 0 : (_maskEvent$masked = maskEvent.masked) === null || _maskEvent$masked === void 0 ? void 0 : _maskEvent$masked.date; // this can be null at runtime when input is partial date or empty.

      switch (mode) {
        case 'start':
          {
            if (newDate !== (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.minValue)) {
              if (newDate) {
                setCalendarDate(newDate);
                setVisibleMonthDate(newDate);
              } else {
                setCalendarDate(new Date());
              }
              processChanges('start', newDate);
            }
            break;
          }
        case 'end':
          {
            if (newDate !== (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.maxValue)) {
              if (newDate) {
                setCalendarDate(newDate);
                setVisibleMonthDate(newDate);
              } else {
                setCalendarDate(new Date());
              }
              processChanges('end', newDate);
            }
            break;
          }
      }
    };
  }, [calendarMode, processChanges, selectedDates]);

  // useWhatChanged(
  //   [
  //     handleEndDateOnFocus,
  //     handleOnDateChangeRaw,
  //     handleOnKeypress,
  //     handleStartDateOnFocus,
  //     maskOptions,
  //     placeHolder,
  //     selectedDates
  //   ],
  //   'handleEndDateOnFocus, handleOnDateChangeRaw, handleOnKeypress, handleStartDateOnFocus, maskOptions, placeHolder, selectedDates'
  // )

  var InputRange = useMemo(function () {
    // eslint-disable-next-line no-console
    console.log("update input ".concat(JSON.stringify(selectedDates)));
    return /*#__PURE__*/React.createElement(Grid, {
      gap: "sm",
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Input, {
      placeholder: placeholder || maskedPlaceholder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ,
      maskOptions: maskOptions,
      onFocus: handleStartDateOnFocus,
      selectedValue: (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.minValue) || undefined,
      onChange: handleOnDateChangeRaw('start'),
      rootComponent: FlexInline,
      onKeyPress: handleOnKeypress('end')
    }), /*#__PURE__*/React.createElement(Text, null, "\u2013"), /*#__PURE__*/React.createElement(Input, {
      placeholder: placeholder || maskedPlaceholder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ,
      maskOptions: maskOptions,
      onFocus: handleEndDateOnFocus,
      selectedValue: (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.maxValue) || undefined,
      onChange: handleOnDateChangeRaw('end'),
      rootComponent: FlexInline,
      onKeyPress: handleOnKeypress('start')
    }));
  }, [handleEndDateOnFocus, handleOnDateChangeRaw, handleOnKeypress, handleStartDateOnFocus, maskOptions, placeholder, maskedPlaceholder, selectedDates]);
  return /*#__PURE__*/React.createElement(Box, null, label ? /*#__PURE__*/React.createElement(Box, {
    className: "date-range-input-container",
    pt: "xxl",
    pb: "md",
    px: "md"
  }, /*#__PURE__*/React.createElement(FormField, {
    label: label
  }, InputRange)) : /*#__PURE__*/React.createElement(Box, {
    className: "date-range-input-container",
    p: "md"
  }, InputRange), /*#__PURE__*/React.createElement(Collapse, {
    "in": !!calendarMode,
    timeout: "auto",
    unmountOnExit: true,
    onExiting: onDatePickerExiting,
    onExited: onDatePickerExited
  }, /*#__PURE__*/React.createElement(Box, {
    className: "calendar-container",
    relative: true,
    bg: "neutral.lighter"
  }, /*#__PURE__*/React.createElement(Box, {
    as: "span",
    fontSize: ".71em",
    color: "neutral.light",
    top: "0",
    left: calendarMode === 'start' ? '10%' : '80%',
    sx: upArrowStyle
  }), /*#__PURE__*/React.createElement(Box, {
    as: "span",
    fontSize: ".7em",
    color: "neutral.lighter",
    top: "1px",
    left: calendarMode === 'start' ? '10%' : '80%',
    zIndex: "5",
    sx: upArrowStyle
  }), /*#__PURE__*/React.createElement(PrivateDatePicker, {
    inline: true,
    isRtl: theme.direction === 'rtl',
    selected: calendarDate,
    locale: typeof locale === 'string' ? locale : 'en-US',
    dateFormat: dateFormat,
    startDate: (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.minValue) || null,
    endDate: (selectedDates === null || selectedDates === void 0 ? void 0 : selectedDates.maxValue) || undefined,
    onSelect: calendarMode === 'start' ? handleStartDatePickerOnSelect : handleEndDatePickerOnSelect,
    onChange: utils.noop,
    minDate: minDate,
    maxDate: maxDate,
    highlightDates: highlightDates,
    disabledKeyboardNavigation: true,
    onMonthChange: setVisibleMonthDate,
    onYearChange: setVisibleMonthDate,
    fontSize: fontSize,
    fontFamily: fontFamily
  }))));
};

export { DateRangePicker };
//# sourceMappingURL=date-range-picker.esm.js.map
